import {useTranslation} from "react-i18next";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {DatePicker} from "@mui/x-date-pickers";
import {useRegistryFuelCardsRepository} from "../../../api/repositories/RegistryFuelCardsRepository";
import {useRegistryFuelCardUse} from "../constants/useRegistryFuelCardUse";
import {useRegistryFuelCardStatuses} from "../constants/useRegistryFuelCardStatuses";
import {useRegistryFuelCardProviders} from "../constants/useRegistryFuelCardProviders";
import {useRegistryFuelCardBusiness} from "../constants/useRegistryFuelCardBusiness";
import {useHasScope} from "../../../../../config/globalConfig";
import {SCOPE_REGISTRY_FUEL_CARD_ADD, SCOPE_REGISTRY_FUEL_CARD_VIEW_DETAIL} from "../constants/RegistryFuelCardAuth";
import NotAuthorizedDialog from "../../../../authentication/component/NotAuthorizedDialog";
import {CloseRounded} from "@mui/icons-material";
import {AMOUNT_REG_EXP} from "../constants/FuelsConstants";
import dayjs from "dayjs";
import {formatToFloat} from "../../../utils/FormatUtils";

interface RegistryFuelCardModalProps {
    open: boolean,
    setOpen: Function,
    registryFuelCardObj: any,
    isReadonly: boolean,
    copyRegistryCard: boolean
}


const RegistryFuelCardDetail = ({open, setOpen, registryFuelCardObj, isReadonly, copyRegistryCard}: RegistryFuelCardModalProps) => {
    const {t} = useTranslation('finance', {keyPrefix: 'registryFuelCard'});
    const {dispatchGlobalData, globalData} = useContext(GlobalContext);
    const hasScope = useHasScope;
    const {updateRegistryFuelCard, postRegistryCard} = useRegistryFuelCardsRepository();
    const {statuses, getRegistryFuelCardStatuses} = useRegistryFuelCardStatuses();
    const {cardUseList, getRegistryFuelCardUse} = useRegistryFuelCardUse();
    const {providers, getRegistryFuelCardProviders} = useRegistryFuelCardProviders();
    const {business, getRegistryFuelCardBusiness} = useRegistryFuelCardBusiness();
    const [registryFuelCard, setRegistryFuelCard] = useState<any>(undefined);
    const [mobilityStores, setMobilityStores] = useState<any[]>([]);



    useEffect(() => {
        if (open && registryFuelCardObj) {
            setRegistryFuelCard(registryFuelCardObj);

            const mobilityStores = globalData.user.mobilityStores.filter((m:any) => m.sapId !== "0" && m.tenant == registryFuelCardObj.legalEntity).filter((value:any, index:any, self:any) =>
                index === self.findIndex((t:any) => (
                    t.name === value.name
                ))).sort((a:any,b:any) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
            setMobilityStores(mobilityStores)
        }
    }, [open]);


    useEffect(() => {
        if (registryFuelCard) {
            reset(retrieveInitialValues());
        }
    }, [registryFuelCard])

    const retrieveInitialValues = () => {
        if (registryFuelCard) {
            return {
                id: registryFuelCard.id,
                cardNumber: copyRegistryCard ? "" : registryFuelCard.cardNumber ? registryFuelCard.cardNumber : "",
                provider: registryFuelCard.provider ?  registryFuelCard.provider  : "",
                cardUse: registryFuelCard.cardUse ? registryFuelCard.cardUse : "",
                idMsAssignee: registryFuelCard.idMsAssignee ? registryFuelCard.idMsAssignee : "",
                msAssignee: registryFuelCard.msAssignee ?
                    isReadonly ? registryFuelCard.msAssignee :
                        mobilityStores.filter((ms: any) => ms.name === registryFuelCard.msAssignee)[0]
                    : null,
                refereeAssignee: registryFuelCard.refereeAssignee ? registryFuelCard.refereeAssignee : "",
                business: registryFuelCard.business ? registryFuelCard.business : "",
                dueDate: registryFuelCard.dueDate ? registryFuelCard.dueDate : null,
                status: registryFuelCard.status ? registryFuelCard.status : "",
                plafond: registryFuelCard.plafond ? formatToFloat(registryFuelCard.plafond, 0.01) : "",
                note: registryFuelCard.note ? registryFuelCard.note : "",
                modifiedDate: registryFuelCard.modifiedDate ? registryFuelCard.modifiedDate : null,
                dealer: registryFuelCard.dealer ? registryFuelCard.dealer : "",
                legalEntity: registryFuelCard.legalEntity ? registryFuelCard.legalEntity : "",
            }
        } else {
            return {
                id: "",
                cardNumber: "",
                provider: "",
                cardUse: "",
                idMsAssignee: "",
                msAssignee: null,
                refereeAssignee: "",
                business: "",
                dueDate: null,
                status: "",
                plafond: "",
                note: "",
                modifiedDate: null,
                dealer: "",
                legalEntity: ""
            }
        }
    }

    const validationSchema = Yup.object({
        cardNumber: Yup.number().typeError(t('messages.requiredCheckNumber')),
        provider: Yup.string().required(t('messages.requiredCheck')),
        cardUse: Yup.string().required(t('messages.requiredCheck')),
        idMsAssignee: Yup.string().required(t('messages.requiredCheck')),
        msAssignee: Yup.object().required(t('messages.requiredCheck')),
        refereeAssignee: Yup.string(),
        business: Yup.string().required(t('messages.requiredCheck')),
        dueDate: Yup.date().required(t('messages.requiredCheck')).nullable().typeError(t('messages.invalidDate')),
        status: Yup.string().required(t('messages.requiredCheck')),
        plafond: Yup.string().nullable().matches(AMOUNT_REG_EXP, {
            excludeEmptyString: true,
            message: t('messages.invalidPlafond')
        }),
        note: Yup.string()
    });

    const {handleSubmit, control, reset, setValue} = useForm({
        defaultValues: retrieveInitialValues(),
        resolver: yupResolver(validationSchema)
    });

    const onSubmit = (payload: any) => {
        let request = {...payload};
        request.msAssignee = request.msAssignee.name;
        request.plafond *= 100;
        if(copyRegistryCard) {
            request.id = "";
            postRegistryCard(request).then((data) => {
                if (data && data.status === "OK") {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("messages.addedCardSuccess"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    setOpen(false);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`messages.${data.message}`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            })
        } else {
            updateRegistryFuelCard(request).then((data) => {
                if (data && data.status === "OK") {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("messages.updateSuccess"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    setOpen(false);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`messages.${data.message}`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            })
        }
    }


    return (
        <Dialog open={open} maxWidth={false}>
            {(hasScope(SCOPE_REGISTRY_FUEL_CARD_VIEW_DETAIL) || hasScope(SCOPE_REGISTRY_FUEL_CARD_ADD)) ?
                <form onSubmit={
                    handleSubmit(data => onSubmit(data), errors => console.log(errors))} noValidate>
                    <DialogTitle>
                        <Grid container item xs={12}>
                            <Grid item xs={10}>
                                <Typography variant={'body1'} fontWeight={'bold'}>
                                    {copyRegistryCard ? t('details.titleAdd') :
                                        t(isReadonly ? 'details.title' : 'details.titleEdit', {cardNumber: registryFuelCard?.cardNumber})
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={1}>
                                <IconButton sx={{position: 'absolute', right: 16, top: 12,}} onClick={() => {
                                    setOpen(false)
                                }}>
                                    <CloseRounded/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container direction={'column'} rowGap={2}>
                            <Grid container item>
                                <Grid container item columnSpacing={3} rowSpacing={1}>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"cardNumber"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    disabled={!copyRegistryCard}
                                                    id="cardNumber"
                                                    value={field.value}
                                                    label={t("details.cardNumber")}
                                                    type="number"
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"provider"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    style={{width: "100%"}}
                                                    select
                                                    disabled={isReadonly}
                                                    required
                                                    label={t('details.provider')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                    {providers.map((provider) => (
                                                        <MenuItem key={provider.value} value={provider.value}>
                                                            {getRegistryFuelCardProviders(provider.value)}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}/>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"business"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    style={{width: "100%"}}
                                                    select
                                                    required
                                                    disabled={isReadonly}
                                                    label={t('details.business')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                    {business.map((bsn) => (
                                                        <MenuItem key={bsn.value} value={bsn.value}>
                                                            {getRegistryFuelCardBusiness(bsn.value)}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"cardUse"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    style={{width: "100%"}}
                                                    select
                                                    required
                                                    disabled={isReadonly}
                                                    label={t('details.cardUse')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                    {cardUseList.map((cardUse) => (
                                                        <MenuItem key={cardUse.value} value={cardUse.value}>
                                                            {getRegistryFuelCardUse(cardUse.value)}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        {isReadonly ?
                                            <Controller
                                                control={control}
                                                name={"msAssignee"}
                                                render={({field, fieldState}) => (
                                                    <TextField
                                                        fullWidth
                                                        id="msAssignee"
                                                        disabled={true}
                                                        required
                                                        value={field.value}
                                                        label={t("details.msAssignee")}
                                                        type="text"
                                                        onChange={field.onChange}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                    />
                                                )}/>
                                            :
                                            <Controller
                                                control={control}
                                                name={"msAssignee"}
                                                render={({field, fieldState}) => (
                                                    <Autocomplete
                                                        id="dms-list"
                                                        options={mobilityStores}
                                                        getOptionLabel={(option: any) => option.name || ""}
                                                        value={field.value}
                                                        onChange={(event: any, newValue: any) => {
                                                            field.onChange(newValue);
                                                            setValue("idMsAssignee", newValue ? newValue.sapId : "");
                                                        }}
                                                        isOptionEqualToValue={(option: any, value: any) => {
                                                            if (option.hasOwnProperty("name")) {
                                                                return option.name === value.name;
                                                            }
                                                            return option === value;
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField {...params} label={t("details.msAssignee")}
                                                                       variant="standard"/>
                                                        )}
                                                    />
                                                )}/>}
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"idMsAssignee"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    id="idMsAssignee"
                                                    disabled={true}
                                                    required
                                                    value={field.value}
                                                    label={t("details.idMsAssignee")}
                                                    type="text"
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"refereeAssignee"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    id="refereeAssignee"
                                                    value={field.value}
                                                    disabled={isReadonly}
                                                    label={t("details.refereeAssignee")}
                                                    type="text"
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"dueDate"}
                                            render={({field, fieldState}) => (
                                                <DatePicker
                                                    label={t("details.dueDate")}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    disabled={isReadonly}
                                                    renderInput={(params) =>
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            required
                                                            type="text"
                                                            disabled={isReadonly}
                                                            label={t("details.dueDate")}
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}

                                                        />}
                                                    inputFormat={"L"}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"status"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    style={{width: "100%"}}
                                                    select
                                                    required
                                                    disabled={isReadonly}
                                                    label={t('details.status')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                    {statuses.map((status) => (
                                                        <MenuItem key={status.value} value={status.value}>
                                                            {getRegistryFuelCardStatuses(status.value)}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"plafond"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    id="plafond"
                                                    value={field.value}
                                                    disabled={isReadonly}
                                                    label={t("details.plafond")}
                                                    type="text"
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField
                                            fullWidth
                                            disabled={true}
                                            id="modifiedDate"
                                            value={registryFuelCard?.modifiedDate ? dayjs(registryFuelCard.modifiedDate).format("L LT") : ""}
                                            label={t("details.modifiedDate")}
                                            type="text"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            control={control}
                                            name={"note"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    id="note"
                                                    disabled={isReadonly}
                                                    value={field.value}
                                                    label={t("details.note")}
                                                    type="text"
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className="ModalFooter">
                        <Grid container>
                            <Grid container item xs={12} rowGap={2} rowSpacing={1} columnSpacing={3}
                                  justifyContent={"flex-end"}>
                                {!isReadonly ?
                                    <>
                                        <Grid item>
                                            <Button variant={"outlined"} color={"accent"}
                                                    onClick={() => {
                                                        setOpen(false);
                                                    }}>
                                                {t('details.buttons.cancel')}
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <Button color={"accent"} type={"submit"}>
                                                {t('details.buttons.save')}
                                            </Button>
                                        </Grid>
                                    </>
                                    :
                                    <Grid item>
                                        <Button color={"accent"}
                                                onClick={() => {
                                                    setOpen(false);
                                                    reset(retrieveInitialValues());
                                                }}>
                                            {t('details.buttons.close')}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    </DialogActions>
                </form>
                : <NotAuthorizedDialog/>}
        </Dialog>

    );
}

export default RegistryFuelCardDetail;